import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  TextInput,
  ActivityIndicator,
  RefreshControl,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useThemeColors } from "@/providers/ThemeProvider";
import { Profile, UserRoleType } from "@/types";

/**
 * User with their roles for the list view
 */
interface UserListItem extends Profile {
  user_roles: { role: UserRoleType; revoked_at: string | null }[];
}

/**
 * Filter type including dependent
 */
type FilterType = UserRoleType | "all" | "dependent";

/**
 * Role badge component
 */
const RoleBadge = ({ role }: { role: UserRoleType }) => {
  const roleConfig: Record<UserRoleType, { bg: string; text: string; label: string }> = {
    super_admin: { bg: "#FEE2E2", text: "#B91C1C", label: "Super Admin" },
    familie_admin: { bg: "#EDE9FE", text: "#7C3AED", label: "Familie Admin" },
    user: { bg: "#F3F4F6", text: "#4B5563", label: "Gebruiker" },
  };

  const config = roleConfig[role] || roleConfig.user;

  return (
    <View
      className="px-2 py-0.5 rounded-full mr-1"
      style={{ backgroundColor: config.bg }}
    >
      <Text className="text-xs font-medium" style={{ color: config.text }}>
        {config.label}
      </Text>
    </View>
  );
};

/**
 * Dependent badge component (Kind)
 */
const DependentBadge = () => (
  <View
    className="px-2 py-0.5 rounded-full mr-1"
    style={{ backgroundColor: "#DBEAFE" }}
  >
    <Text className="text-xs font-medium" style={{ color: "#1D4ED8" }}>
      Kind
    </Text>
  </View>
);

/**
 * User list item component
 */
interface UserItemProps {
  user: UserListItem;
  onPress: () => void;
}

const UserItem = ({ user, onPress }: UserItemProps) => {
  const colors = useThemeColors();
  // Get active roles (not revoked)
  const activeRoles = user.user_roles
    .filter((r) => !r.revoked_at)
    .map((r) => r.role);

  // Check if user is a dependent (kind)
  const isDependent = user.is_dependent === true;

  return (
    <Pressable
      onPress={onPress}
      className="px-4 py-3 flex-row items-center active:opacity-70"
      style={{
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
      }}
      accessibilityRole="button"
      accessibilityLabel={`Gebruiker ${user.full_name || user.email || "Onbekend"}`}
    >
      {/* Avatar */}
      <View
        className="w-12 h-12 rounded-full items-center justify-center mr-3"
        style={{ backgroundColor: isDependent ? "#DBEAFE" : colors.primaryLight }}
      >
        <Text className="font-bold text-lg" style={{ color: isDependent ? "#1D4ED8" : colors.primary }}>
          {(user.full_name || user.email || "?").charAt(0).toUpperCase()}
        </Text>
      </View>

      {/* User info */}
      <View className="flex-1">
        <Text className="text-base font-semibold" style={{ color: colors.text }}>
          {user.full_name || "Geen naam"}
        </Text>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          {isDependent ? "Geen e-mail (kind)" : user.email}
        </Text>
        <View className="flex-row mt-1 flex-wrap">
          {isDependent ? (
            <DependentBadge />
          ) : activeRoles.length > 0 ? (
            activeRoles.map((role) => <RoleBadge key={role} role={role} />)
          ) : (
            <RoleBadge role="user" />
          )}
        </View>
      </View>

      {/* Chevron */}
      <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
    </Pressable>
  );
};

/**
 * Users management screen
 * Lists all users with search and filter functionality
 */
export default function UsersScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<FilterType>("all");

  /**
   * Fetch all users with their roles
   */
  const fetchUsers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select(`
          *,
          user_roles!user_roles_user_id_fkey (
            role,
            revoked_at
          )
        `)
        .order("full_name", { ascending: true });

      if (error) {
        console.error("Error fetching users:", error);
        return;
      }

      setUsers(data || []);
      setFilteredUsers(data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  /**
   * Filter users based on search query and role filter
   */
  useEffect(() => {
    let filtered = users;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.full_name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.nickname?.toLowerCase().includes(query)
      );
    }

    // Apply role/type filter
    if (roleFilter === "dependent") {
      filtered = filtered.filter((user) => user.is_dependent === true);
    } else if (roleFilter !== "all") {
      filtered = filtered.filter((user) =>
        user.user_roles.some((r) => r.role === roleFilter && !r.revoked_at)
      );
    }

    setFilteredUsers(filtered);
  }, [searchQuery, roleFilter, users]);

  /**
   * Initial data fetch
   */
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchUsers();
  };

  /**
   * Handle user press - navigate to detail
   */
  const handleUserPress = (userId: string) => {
    router.push(`/(super-admin)/users/${userId}`);
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color="#DC2626" />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Gebruikers laden...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      {/* Search and Filter */}
      <View
        className="px-4 py-3"
        style={{
          backgroundColor: colors.surface,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        }}
      >
        {/* Search Input */}
        <View
          className="flex-row items-center rounded-xl px-3 py-2"
          style={{ backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border }}
        >
          <Ionicons name="search" size={20} color={colors.textTertiary} />
          <TextInput
            className="flex-1 ml-2 text-base"
            style={{ color: colors.text }}
            placeholder="Zoek op naam of email..."
            placeholderTextColor={colors.textTertiary}
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {searchQuery.length > 0 && (
            <Pressable
              onPress={() => setSearchQuery("")}
              accessibilityRole="button"
              accessibilityLabel="Wis zoekopdracht"
            >
              <Ionicons name="close-circle" size={20} color={colors.textTertiary} />
            </Pressable>
          )}
        </View>

        {/* Role Filter */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          className="mt-3 -mx-1"
          contentContainerClassName="px-1"
        >
          {(["all", "super_admin", "familie_admin", "user", "dependent"] as const).map((filter) => {
            const isActive = roleFilter === filter;
            const labels: Record<typeof filter, string> = {
              all: "Alle",
              super_admin: "Super Admin",
              familie_admin: "Familie Admin",
              user: "Gebruiker",
              dependent: "Kind",
            };

            return (
              <Pressable
                key={filter}
                onPress={() => setRoleFilter(filter)}
                className="px-4 py-1.5 rounded-full mr-2"
                style={{
                  backgroundColor: isActive ? (filter === "dependent" ? "#1D4ED8" : "#DC2626") : colors.surface,
                  borderWidth: isActive ? 0 : 1,
                  borderColor: colors.border,
                }}
                accessibilityRole="button"
                accessibilityLabel={`Filter op ${labels[filter]}`}
              >
                <Text
                  className="text-sm font-medium"
                  style={{ color: isActive ? "#FFFFFF" : colors.textSecondary }}
                >
                  {labels[filter]}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>
      </View>

      {/* Users Count */}
      <View className="px-4 py-2" style={{ backgroundColor: colors.background }}>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          {filteredUsers.length} gebruiker{filteredUsers.length !== 1 ? "s" : ""} gevonden
        </Text>
      </View>

      {/* Users List */}
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor="#DC2626"
          />
        }
      >
        {filteredUsers.length > 0 ? (
          filteredUsers.map((user) => (
            <UserItem
              key={user.id}
              user={user}
              onPress={() => handleUserPress(user.id)}
            />
          ))
        ) : (
          <View className="flex-1 items-center justify-center py-12">
            <Ionicons name="people-outline" size={48} color={colors.border} />
            <Text className="mt-2" style={{ color: colors.textTertiary }}>
              Geen gebruikers gevonden
            </Text>
          </View>
        )}
        {/* Bottom spacing */}
        <View className="h-4" />
      </ScrollView>
    </SafeAreaView>
  );
}
